/**
 * Environmental Geotech — Geohazard Risk  (standalone module)
 *
 * Landslide detection, flood risk assessment, and slope stability monitoring
 * using Sentinel-1 SAR coherence, DEM analysis, and multispectral indices.
 * Each geohazard zone can be escalated to a civil engineering inspection or
 * early-warning alert for downstream communities.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Mountain, RefreshCw, Zap, AlertOctagon,
  ChevronDown, ChevronUp, CheckCircle2, XCircle, ShieldAlert,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Modal from '../../components/common/Modal';
import Alert from '../../components/common/Alert';
import { anomalyApi, satelliteBoundariesApi } from '../../services/api';

// ─── Types ────────────────────────────────────────────────────────────────────

type Severity      = 'low' | 'medium' | 'high' | 'critical';
type AnomalyStatus = 'pending' | 'investigating' | 'resolved' | 'false_positive';

interface GeohazardAnomaly {
  id: string;
  boundary_name: string;
  boundary_id: string;
  anomaly_type: string;
  severity: Severity;
  status: AnomalyStatus;
  change_percentage: number | string;
  affected_area_hectares?: number;
  description?: string;
  detection_date: string;
  resolution_notes?: string;
}

// ─── Escalation options ───────────────────────────────────────────────────────

const ESCALATION_ACTIONS = [
  {
    id: 'engineer_inspect',
    label: 'Dispatch Civil Engineering Inspection',
    icon: Mountain,
    newStatus: 'investigating' as AnomalyStatus,
    notePrefix: '[GEOHAZARD: Engineering inspection dispatched] ',
    badge: 'site visit',
    badgeClass: 'bg-blue-100 text-blue-700',
  },
  {
    id: 'early_warning',
    label: 'Issue Early-Warning Alert to Communities',
    icon: ShieldAlert,
    newStatus: 'investigating' as AnomalyStatus,
    notePrefix: '[GEOHAZARD: Early-warning alert issued] ',
    badge: 'public safety',
    badgeClass: 'bg-red-100 text-red-700',
  },
  {
    id: 'monitor',
    label: 'Monitor — Increase Satellite Pass Frequency',
    icon: CheckCircle2,
    newStatus: 'pending' as AnomalyStatus,
    notePrefix: '[GEOHAZARD: Monitoring escalated] ',
    badge: null,
    badgeClass: '',
  },
  {
    id: 'false_positive',
    label: 'Reject — False Positive',
    icon: XCircle,
    newStatus: 'false_positive' as AnomalyStatus,
    notePrefix: '[GEOHAZARD: False positive] ',
    badge: null,
    badgeClass: '',
  },
];

// ─── Style maps ───────────────────────────────────────────────────────────────

const SEVERITY_STYLE: Record<Severity, string> = {
  low:      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  medium:   'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  high:     'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  critical: 'bg-red-200 text-red-900 font-bold dark:bg-red-900/60 dark:text-red-200',
};

const STATUS_STYLE: Record<AnomalyStatus, string> = {
  pending:        'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  investigating:  'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  resolved:       'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  false_positive: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
};

const STATUS_LABELS: Record<AnomalyStatus, string> = {
  pending:        'Pending',
  investigating:  'Action Taken',
  resolved:       'Resolved',
  false_positive: 'False Positive',
};

// ─── Component ────────────────────────────────────────────────────────────────

export const GeohazardRiskPage: React.FC = () => {
  const [anomalies, setAnomalies]   = useState<GeohazardAnomaly[]>([]);
  const [zones, setZones]           = useState<{ id: string; name: string }[]>([]);
  const [stats, setStats]           = useState<any>(null);
  const [loading, setLoading]       = useState(true);
  const [scanning, setScanning]     = useState(false);
  const [error, setError]           = useState<string | null>(null);
  const [success, setSuccess]       = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const [filterZone,     setFilterZone]     = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus,   setFilterStatus]   = useState('');

  const [reviewing,   setReviewing]   = useState<GeohazardAnomaly | null>(null);
  const [actionId,    setActionId]    = useState('');
  const [actionNote,  setActionNote]  = useState('');
  const [submitting,  setSubmitting]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [anomalyRes, statsRes] = await Promise.all([
        anomalyApi.getAnomalies({
          severity:   filterSeverity || undefined,
          status:     filterStatus   || undefined,
          boundaryId: filterZone     || undefined,
          vertical:   'environmental',
        }),
        anomalyApi.getStats('environmental'),
      ]);
      // Filter to geohazard anomaly types
      const all = (anomalyRes.anomalies ?? []) as GeohazardAnomaly[];
      setAnomalies(
        all.filter((a) => /landslide|flood|slope|geohazard|sar|dem|subsidence|erosion/i.test(a.anomaly_type))
      );
      setStats(statsRes);
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Failed to load geohazard data');
    } finally {
      setLoading(false);
    }
  }, [filterSeverity, filterStatus, filterZone]);

  useEffect(() => {
    load();
    satelliteBoundariesApi.getAll()
      .then((res) => {
        const all = res.data?.boundaries ?? [];
        setZones(
          all.filter((b: any) =>
            b.metadata?.vertical === 'environmental' &&
            b.metadata?.zone_type === 'geohazard_zone'
          )
        );
      })
      .catch(() => {});
  }, [load]);

  const handleScan = async () => {
    setScanning(true); setError(null);
    try {
      const res = await anomalyApi.runMonitoring('environmental');
      setSuccess(`SAR / DEM scan complete — ${res.anomaliesDetected ?? 0} geohazard event(s) detected`);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleEscalate = async () => {
    if (!reviewing || !actionId) return;
    const action = ESCALATION_ACTIONS.find((a) => a.id === actionId)!;
    setSubmitting(true);
    try {
      await anomalyApi.updateStatus(reviewing.id, {
        status: action.newStatus,
        notes:  `${action.notePrefix}${actionNote}`.trim(),
      });
      setSuccess(`Decision recorded: ${action.label}`);
      setReviewing(null);
      setActionId('');
      setActionNote('');
      await load();
    } catch (e: any) {
      const d = e?.response?.data;
      setError(d?.error?.message ?? (typeof d?.error === 'string' ? d.error : null) ?? d?.message ?? e?.message ?? 'Failed to save decision');
    } finally {
      setSubmitting(false);
    }
  };

  const pendingCount = anomalies.filter((a) => a.status === 'pending').length;

  const SELECT_CLS = 'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Mountain className="w-7 h-7 text-orange-600" /> Geohazard Risk
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Landslide detection, flood risk, and slope stability assessment using SAR coherence and DEM analysis
          </p>
          {pendingCount > 0 && (
            <span className="inline-flex items-center gap-1 mt-2 px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-800">
              <Zap className="w-3 h-3" /> {pendingCount} geohazard event{pendingCount !== 1 ? 's' : ''} pending
            </span>
          )}
        </div>
        <Button onClick={handleScan} loading={scanning} variant="outline">
          <Zap className="w-4 h-4 mr-1" /> SAR / DEM Scan
        </Button>
      </div>

      {error   && <Alert type="error"   onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert type="success" onClose={() => setSuccess(null)}>{success}</Alert>}

      {/* ── Stats ── */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Events', value: anomalies.length,                                                col: 'text-gray-900 dark:text-white' },
            { label: 'Critical',     value: anomalies.filter((a) => a.severity === 'critical').length,      col: 'text-red-700 dark:text-red-400' },
            { label: 'Pending',      value: pendingCount,                                                   col: 'text-amber-700 dark:text-amber-400' },
            { label: 'Action Taken', value: anomalies.filter((a) => a.status === 'investigating').length,   col: 'text-blue-700 dark:text-blue-400' },
          ].map(({ label, value, col }) => (
            <Card key={label} padding="sm">
              <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
              <p className={`text-2xl font-bold mt-1 ${col}`}>{value}</p>
            </Card>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <Card padding="sm">
        <div className="flex flex-wrap gap-3 items-center">
          <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} className={SELECT_CLS}>
            <option value="">All Severities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className={SELECT_CLS}>
            <option value="">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="investigating">Action Taken</option>
            <option value="resolved">Resolved</option>
            <option value="false_positive">False Positive</option>
          </select>
          <select value={filterZone} onChange={(e) => setFilterZone(e.target.value)} className={SELECT_CLS}>
            <option value="">All Zones</option>
            {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
          </select>
          <Button size="sm" onClick={load}>Apply</Button>
          {(filterSeverity || filterStatus || filterZone) && (
            <button onClick={() => { setFilterSeverity(''); setFilterStatus(''); setFilterZone(''); }}
              className="text-xs text-gray-500 hover:text-gray-700 underline">Clear</button>
          )}
        </div>
      </Card>

      {/* ── Geohazard event list ── */}
      <Card padding="none">
        <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {anomalies.length} geohazard event{anomalies.length !== 1 ? 's' : ''}
          </p>
          <p className="text-xs text-gray-400">Click <strong>Escalate</strong> to take action</p>
        </div>

        {loading ? (
          <div className="flex justify-center py-14"><Spinner size="lg" /></div>
        ) : anomalies.length === 0 ? (
          <div className="text-center py-14">
            <Mountain className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No geohazard events detected</p>
            <p className="text-sm text-gray-400 mt-1">Click <strong>SAR / DEM Scan</strong> to check for slope instability</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 dark:divide-gray-800">
            {anomalies.map((a) => {
              const isExpanded = expandedId === a.id;
              return (
                <div key={a.id}>
                  <button
                    className="w-full flex items-center gap-4 px-5 py-4 hover:bg-gray-50 dark:hover:bg-gray-700/30 text-left"
                    onClick={() => setExpandedId(isExpanded ? null : a.id)}
                  >
                    <AlertOctagon className={`w-4 h-4 flex-shrink-0 ${a.severity === 'critical' ? 'text-red-500' : 'text-orange-500'}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{a.boundary_name}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {a.anomaly_type?.replace(/_/g, ' ')} ·{' '}
                        {a.detection_date ? new Date(a.detection_date).toLocaleDateString('en-GB') : '—'}
                      </p>
                    </div>
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium capitalize ${SEVERITY_STYLE[a.severity]}`}>{a.severity}</span>
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${STATUS_STYLE[a.status]}`}>{STATUS_LABELS[a.status]}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                  </button>

                  {isExpanded && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 dark:bg-gray-800/50 border-t border-gray-100 dark:border-gray-700 space-y-3">
                      <div className="flex flex-wrap gap-6 text-xs text-gray-500 dark:text-gray-400">
                        <span>SAR Coherence Δ: <strong className="text-orange-600 dark:text-orange-400">
                          {a.change_percentage != null ? `${Number(a.change_percentage).toFixed(1)}%` : '—'}
                        </strong></span>
                        {a.affected_area_hectares != null && (
                          <span>Affected area: <strong className="text-gray-800 dark:text-gray-200">{Number(a.affected_area_hectares).toFixed(1)} ha</strong></span>
                        )}
                      </div>
                      {a.description && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 italic">{a.description}</p>
                      )}
                      {a.resolution_notes && (
                        <p className="text-xs text-gray-500 dark:text-gray-500 bg-gray-100 dark:bg-gray-800 rounded px-3 py-2">{a.resolution_notes}</p>
                      )}
                      <Button size="sm" variant={a.status === 'pending' ? 'primary' : 'outline'}
                        onClick={() => { setReviewing(a); setActionId(''); setActionNote(''); }}>
                        Escalate
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ── Escalation modal ── */}
      {reviewing && (
        <Modal onClose={() => setReviewing(null)} panelClassName="max-w-lg p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold text-gray-900 dark:text-white">Escalate Geohazard</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{reviewing.boundary_name}</p>
              </div>
              <span className={`px-2 py-0.5 rounded text-xs font-medium capitalize ${SEVERITY_STYLE[reviewing.severity]}`}>
                {reviewing.severity}
              </span>
            </div>

            <div className="rounded-lg bg-gray-50 dark:bg-gray-800 p-3 mb-4 text-sm">
              <p className="font-semibold text-gray-700 dark:text-gray-300 mb-1">SAR / DEM Analysis</p>
              <p className="text-gray-600 dark:text-gray-400">
                Type: <strong>{reviewing.anomaly_type?.replace(/_/g, ' ')}</strong> &nbsp;·&nbsp;
                Coherence Δ: <strong className="text-orange-600">{reviewing.change_percentage != null ? `${Number(reviewing.change_percentage).toFixed(1)}%` : '—'}</strong>
                {reviewing.affected_area_hectares != null && (
                  <> &nbsp;·&nbsp; <strong>{Number(reviewing.affected_area_hectares).toFixed(1)} ha</strong> affected</>
                )}
              </p>
            </div>

            <div className="space-y-2 mb-4">
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">Action</p>
              {ESCALATION_ACTIONS.map((action) => {
                const Icon = action.icon;
                return (
                  <button
                    key={action.id}
                    onClick={() => setActionId(action.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg border-2 text-sm font-medium text-left transition-all ${actionId === action.id ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20' : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'}`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0 text-gray-500" />
                    {action.label}
                    {action.badge && (
                      <span className={`ml-auto text-xs px-1.5 py-0.5 rounded ${action.badgeClass}`}>{action.badge}</span>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="mb-5">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Note (optional)</label>
              <textarea rows={2} value={actionNote} onChange={(e) => setActionNote(e.target.value)}
                placeholder="Community contacts, slope engineering firm, evacuation route, risk register reference…"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm dark:text-white resize-none focus:outline-none focus:ring-2 focus:ring-primary-500" />
            </div>

            <div className="flex gap-3">
              <Button onClick={handleEscalate} disabled={!actionId || submitting} loading={submitting} className="flex-1">
                Confirm Action
              </Button>
              <Button variant="outline" onClick={() => setReviewing(null)} disabled={submitting} className="flex-1">
                Cancel
              </Button>
            </div>
        </Modal>
      )}
    </div>
  );
};

export default GeohazardRiskPage;

// Made with Bob
